import { ref, watch, onMounted, onUnmounted } from 'vue';
import { disableScrollOnModalOpen, splitByNewlines } from 'src/utils/helper/misc';
import {
  MdButton,
  MdTypography,
  MdModal,
  MdIconClose,
  MdIconReload,
  MdSpinner,
  MdLink
} from '@antiqua-dls/vue-components';
import SmartStars from 'src/components/icons/smart-stars';
import { useI18n } from 'vue-i18n';
import WarningIcon from 'src/components/icons/warning-icon';
import { Toaster } from 'src/utils/helper/toaster';
import LoadingSkeleton from 'src/pages/inventory-ingestion-hub/components/ingestion-loader/index.vue';

import { useAiTextEditor } from '../../../../../../composables/useAiTextEditor/index.js';

export default {
  name: 'AiConsentModal',
  components: {
    MdButton,
    MdTypography,
    MdModal,
    MdIconClose,
    MdSpinner,
    SmartStars,
    MdIconReload,
    WarningIcon,
    MdLink,
    LoadingSkeleton
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    updateConsent: {
      type: Boolean,
      default: true
    }
  },
  emits: ['cancel', 'enable-ai'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { getAiAgreement, updateAiConsentForFoundry } = useAiTextEditor();

    const agreement = ref({
      isLoading: true,
      text: null,
      metadata: null,
      action: false
    });
    const errorState = ref({
      status: false,
      message: null
    });
    const inProgress = ref(false);

    // Static value no need to be ref
    const loadingSkeletonChild = [
      {
        height: '11px',
        width: '70%'
      },
      {
        height: '10px',
        width: '100%'
      },
      {
        height: '10px',
        width: '75%'
      }
    ];
    const loadingSkeleton = [loadingSkeletonChild, loadingSkeletonChild, loadingSkeletonChild];

    watch(
      () => props.show,
      newVal => {
        disableScrollOnModalOpen(newVal, true);
      },
      { immediate: true }
    );

    const agreeAction = async () => {
      if (!props.updateConsent) {
        emit('enable-ai', agreement.value.metadata.id);
        return;
      }

      inProgress.value = true;
      const response = await updateAiConsentForFoundry(agreement.value.metadata.id);
      if (response) {
        emit('enable-ai');
        inProgress.value = false;
      } else {
        Toaster.notifyError(t('pages.contract.error_message'));
        inProgress.value = false;
      }
    };

    const fetchConsentAgreement = () => {
      agreement.value = {
        isLoading: true,
        text: null,
        metadata: null,
        action: false
      };
      getAiAgreement().then(response => {
        if (response.success) {
          const textArray = splitByNewlines(response.data.text);
          agreement.value = {
            isLoading: false,
            text: textArray,
            metadata: response.data,
            action: true
          };
          errorState.value = {
            status: false,
            message: null
          };
        } else {
          agreement.value = {
            isLoading: false,
            text: null,
            action: false
          };
          errorState.value = {
            status: true,
            message: t('pages.aiTextEditor.ai_consent_modal.body_error_message')
          };
        }
      });
      // catch is not needed already handled in composable
    };

    onMounted(() => {
      fetchConsentAgreement();
    });

    onUnmounted(() => {
      disableScrollOnModalOpen(false, true);
    });

    return {
      agreement,
      agreeAction,
      inProgress,
      errorState,
      fetchConsentAgreement,
      loadingSkeleton
    };
  }
};
